import firebaseApp from '../firebase/index';
import {
  getCurrentUser,
  getPublicKey,
  getUserIdByPublicAddress,
  getPrivateKey,
  getDecryptKey,
  saveDecryptKey
} from './user.service';
import CustomError from './errors/CustomError';
import { status_response } from './constants';
import globalConfig from '../config';
import { encrypt, md5 } from './crypto.service';
import axios from 'axios';

const userRef = firebaseApp.firestore().collection('users');
const userPrivateInfoRef = firebaseApp.firestore().collection('users-private-information');
const FIREBASE_SERVER_URL = globalConfig.FIREBASE_SERVER_URL;

/**
 * Get profile user
 */
const getUserProfile = async function(user) {
  try {
    if (user == null || typeof user === 'undefined') {
      user = await getCurrentUser();
    }
    const userId = user.uid;
    const docProfile = await userRef.doc(userId).get();
    const profile = docProfile.data();
    return {
      status: status_response.success,
      result: {
        data: profile || {},
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Update user's email address
 * An email will be sent to the original email address (if it was set)
 * that allows to revoke the email address change, in order to protect them from account hijacking.
 * Important: this is a security sensitive operation that requires the user to have recently signed in.
 * If this requirement isn't met, ask the user to authenticate again
 * and then call firebase.User#reauthenticateWithCredential.
 *
 * Error Codes FROM FIREBASE:
 * auth/invalid-email
 *            Thrown if the email used is invalid.
 * auth/email-already-in-use
 *            Thrown if the email is already used by another user.
 * auth/requires-recent-login
 *            Thrown if the user's last sign-in time does not meet the security threshold.
 *            Use firebase.User#reauthenticateWithCredential to resolve.
 *            This does not apply if the user is anonymous. Error Codes
 * @param user
 * @param newEmailAddr
 * @returns {Promise<void>}
 */
const changeUserEmailAddress = async function(user, newEmailAddr) {
  try {
    if (user == null || typeof user === 'undefined') {
      user = await getCurrentUser();
    }
    await userRef.doc(user.uid).update({ loginId: newEmailAddr || null });
    return user
      .updateEmail(newEmailAddr)
      .then(() => {
        // after update email successful, we send the email verification to user new email address to confirm
        return user.sendEmailVerification();
      })
      .then(response => {
        return {
          status: status_response.success,
          result: {
            data: user,
            messageId: 'EF2181_Content'
          }
        };
      })
      .catch(error => {
        if (error.code === 'auth/invalid-email') {
          return {
            status: status_response.error,
            result: {
              messageId: 'EF2004_Content',
              message: error.message
            }
          };
        } else if (error.code === 'auth/email-already-in-use') {
          return {
            status: status_response.error,
            result: {
              messageId: 'EF2001_Content',
              message: error.message
            }
          };
        } else if (error.code === 'auth/requires-recent-login') {
          return {
            status: status_response.error,
            result: {
              messageId: 'EF2007_Content',
              message: error.message,
              errorCode: 'auth/requires-recent-login'
            }
          };
        }
        console.log(error);
        return {
          status: status_response.error,
          result: {
            message: error.message,
            messageId: 'EF2182_Content'
          }
        };
      });
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2182_Content'
      }
    };
  }
};

/**
 * Update user avatar
 * @param user the login current user
 * @param photoUrl hyperlink to image photo. Image must be uploaded to a public access storage
 * @returns {Promise<*>}
 */
const updateUserAvatar = async function(user, photoUrl) {
  try {
    if (photoUrl == null || typeof photoUrl === 'undefined' || photoUrl.length === 0) {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2208_content'
        }
      };
    }
    if (user == null || typeof user === 'undefined') {
      user = await getCurrentUser();
    }

    // Passing a null value will delete the current attribute's value, but not
    // passing a property won't change the current attribute's value
    return user
      .updateProfile({
        photoURL: photoUrl
      })
      .then(profile => {
        userRef.doc(user.uid).update({ avatar: user.photoURL || null });

        return {
          status: status_response.success,
          result: {
            data: profile,
            messageId: 'EF2207_Content'
          }
        };
      })
      .catch(error => {
        console.log(error);
        return {
          status: status_response.error,
          result: {
            message: error.message,
            messageId: 'EF2208_Content'
          }
        };
      });
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2208_Content'
      }
    };
  }
};

/**
 * Update current user's display name
 * @param user current firebase user, which contains the user.uid
 * @param newUsername the new display name to change to
 */
const updateUsername = async function(user, newUsername) {
  try {
    if (newUsername == null || typeof newUsername === 'undefined' || newUsername.length === 0) {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2002_Content'
        }
      };
    }
    if (user == null || typeof user === 'undefined') {
      user = await getCurrentUser();
    }

    // Passing a null value will delete the current attribute's value, but not
    // passing a property won't change the current attribute's value
    return user
      .updateProfile({
        displayName: newUsername
      })
      .then(() => {
        userRef.doc(user.uid).update({ userName: user.displayName || null });
        updateCustomerNameOrderElastic(user, user.displayName);
        return {
          status: status_response.success,
          result: {
            data: user,
            messageId: 'EF2179_Content'
          }
        };
      })
      .catch(error => {
        console.log(error);
        return {
          status: status_response.error,
          result: {
            message: error.message,
            messageId: 'EF2180_Content'
          }
        };
      });
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2180_Content'
      }
    };
  }
};

const updateCustomerNameOrderElastic = async function(user, newName) {
  try {
    if (!user) {
      user = await getCurrentUser();
    }
    const url = `${FIREBASE_SERVER_URL}/elasticsearch/ecb_orders/_doc/_update_by_query`;
    let token = await user.getIdToken();
    let pubkey = await getPublicKey(user);
    let config = {
      headers: { Authorization: 'Bearer ' + token }
    };
    const sendData = {
      query: {
        bool: {
          must: [
            {
              match: {
                cusAddr: pubkey
              }
            }
          ]
        }
      },
      data: {
        customerName: newName
      }
    };
    return axios.put(url, sendData, config);
  } catch (error) {
    return error;
  }
};

const updatePassword = async function(newPassword) {
  try {
    const user = await getCurrentUser();
    const decyptKey = getDecryptKey();
    const privateKey = await getPrivateKey(user);
    await userPrivateInfoRef.doc(user.uid).update({
      encryptPrivateKey: encrypt(md5(newPassword), privateKey)
    });
    try {
      await user.updatePassword(newPassword);
      saveDecryptKey(md5(newPassword));
      return {
        status: status_response.success,
        result: {
          message: 'success',
          messageId: 'EF2212_Content'
        }
      };
    } catch (error) {
      console.log(error);
      await userPrivateInfoRef.doc(user.uid).update({
        encryptPrivateKey: encrypt(decyptKey, privateKey)
      });
      if (error.code === 'auth/requires-recent-login') {
        return {
          status: status_response.error,
          result: {
            messageId: 'EA2085_Content',
            message: error.message
          }
        };
      }
      return {
        status: status_response.error,
        result: {
          message: error.message,
          messageId: 'EA2085_Content'
        }
      };
    }
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EA2085_Content'
      }
    };
  }
};

const getUserProfileByAddress = async function(userAddress) {
  try {
    const userId = await getUserIdByPublicAddress(userAddress);
    const docProfile = await userRef.doc(userId).get();
    const profile = docProfile.data();
    const result = {};
    if (profile) {
      result.image = profile.avatar;
      result.name = profile.userName;
    }
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Get list delivery address of user
 */
const getDeliveryAddressInfo = async function() {
  try {
    let { uid: userId } = await getCurrentUser();
    const docProfile = await userRef.doc(userId).get();
    const profile = docProfile.data() || {};
    const { deliveryAddrInfo } = profile;
    return {
      status: status_response.success,
      result: {
        data: deliveryAddrInfo || [],
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Add new delivery address
 * @param {*} receiverName
 * @param {*} zipcode
 * @param {*} prefecture
 * @param {*} address
 * @param {*} phoneNumber
 */
const addDeliveryAddress = async function(receiverName, zipcode, prefecture, address, phoneNumber) {
  try {
    checkReceiverName(receiverName);
    checkZipCode(zipcode);
    checkPrefecture(prefecture);
    checkAddress(address);
    checkPhoneNumber(phoneNumber);
    let { status, result } = await getDeliveryAddressInfo();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    let deliveryAddrInfo = result.data;
    deliveryAddrInfo.unshift({
      id: new Date().getTime(),
      receiverName,
      zipcode,
      prefecture,
      address,
      phoneNumber,
      isDefault: false
    });

    let { uid: userId } = await getCurrentUser();
    userRef.doc(userId).update({
      deliveryAddrInfo
    });
    return {
      status: status_response.success,
      result: {
        data: deliveryAddrInfo,
        messageId: 'EF2203_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Remove a delivery address by id
 * @param {*} id
 */
const removeDeliveryAddress = async function(id) {
  try {
    const { status, result } = await getDeliveryAddressInfo();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    const deliveryAddrInfo = result.data;
    const newDeliveryAddressInfo = deliveryAddrInfo.filter(deliveryAddress => deliveryAddress.id !== id);
    let { uid: userId } = await getCurrentUser();
    userRef.doc(userId).update({
      deliveryAddrInfo: newDeliveryAddressInfo
    });
    return {
      status: status_response.success,
      result: {
        data: newDeliveryAddressInfo,
        messageId: 'EF2205_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2206_Content'
      }
    };
  }
};

/**
 * Update delivery address by id
 * @param {*} id
 * @param {*} receiverName
 * @param {*} zipcode
 * @param {*} prefecture
 * @param {*} address
 * @param {*} phoneNumber
 */
const updateDeliveryAddress = async function(id, receiverName, zipcode, prefecture, address, phoneNumber) {
  try {
    checkReceiverName(receiverName);
    checkZipCode(zipcode);
    checkPrefecture(prefecture);
    checkAddress(address);
    checkPhoneNumber(phoneNumber);
    const { status, result } = await getDeliveryAddressInfo();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    const deliveryAddrInfo = result.data;
    const index = deliveryAddrInfo.findIndex(x => x.id.toString() === id.toString());
    if (index === -1) {
      throw new CustomError('Not Found delivery to update', 'EA2015_Content');
    }
    deliveryAddrInfo[index] = {
      id,
      receiverName,
      zipcode,
      prefecture,
      address,
      phoneNumber,
      isDefault: deliveryAddrInfo[index].isDefault
    };
    let { uid: userId } = await getCurrentUser();
    userRef.doc(userId).update({
      deliveryAddrInfo: deliveryAddrInfo
    });
    return {
      status: status_response.success,
      result: {
        data: deliveryAddrInfo,
        messageId: 'EF2203_Content'
      }
    };
  } catch (error) {
    console.log(`profile.service.js - (247) - `, error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2204_Content'
      }
    };
  }
};

/**
 * Set default a delivery address
 * @param {*} id
 */
const setDefaultDeliveryAddress = async function(id) {
  try {
    const { status, result } = await getDeliveryAddressInfo();
    if (!status) {
      throw new CustomError(result.message, result.messageId);
    }
    const deliveryAddrInfo = result.data;
    deliveryAddrInfo.forEach(item => {
      item.isDefault = item.id === id;
    });
    let { uid: userId } = await getCurrentUser();
    userRef.doc(userId).update({
      deliveryAddrInfo
    });
    return {
      status: status_response.success,
      result: {
        data: deliveryAddrInfo,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Check valid receiverName input
 * @param {*} receiverName
 */
const checkReceiverName = function(receiverName) {
  if (receiverName == null || typeof receiverName === 'undefined' || !receiverName.length) {
    throw new CustomError('Receiver name is require!', 'EA2015_Content');
  } else if (receiverName.length > 30) {
    throw new CustomError('Receiver name require less than 30 characters', 'EA2015_Content');
  }
};

/**
 * Check valid zipcode input
 * @param {*} zipcode
 */
const checkZipCode = function(zipcode) {
  if (!zipcode || !zipcode.length) {
    throw new CustomError('Zipcode is require!', 'EA2015_Content');
  } else if (zipcode.length !== 8 || zipcode.indexOf('-') !== 3 || zipcode.indexOf('_') > 0) {
    throw new CustomError('Zipcode require equal 7 characters', 'EA2015_Content');
  }
};

/**
 * Check valid prefecture input
 * @param {*} prefecture
 */
const checkPrefecture = function(prefecture) {
  if (!prefecture || !prefecture.length) {
    throw new CustomError('Prefecture is require!', 'EA2015_Content');
  } else if (prefecture.length >= 30) {
    throw new CustomError('Prefecture require less than 30 characters', 'EA2015_Content');
  }
};

/**
 * Check valid address input
 * @param {*} address
 */
const checkAddress = function(address) {
  if (!address || !address.length) {
    throw new CustomError('Address is require!', 'EA2015_Content');
  } else if (address.length > 100) {
    throw new CustomError('Address require less than 100 characters', 'EA2015_Content');
  }
};

/**
 * Check valid phone numnber input
 * @param {*} phone
 */
const checkPhoneNumber = function(phone) {
  if (!phone || !phone.length) {
    throw new CustomError('Phone is require!', 'EA2015_Content');
  } else if (phone.length !== 10 && phone.length !== 11) {
    throw new CustomError('Phone require 10 or 11 number', 'EA2015_Content');
  }
};

export {
  getUserProfile,
  updateUsername,
  updateUserAvatar,
  updatePassword,
  changeUserEmailAddress,
  getDeliveryAddressInfo,
  addDeliveryAddress,
  updateDeliveryAddress,
  removeDeliveryAddress,
  getUserProfileByAddress,
  setDefaultDeliveryAddress,
  updateCustomerNameOrderElastic
};
